import { useDrag } from '@use-gesture/react';
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { animated, config, useSpring } from 'react-spring';
import styled, { CSSProperties } from 'styled-components';

import { Button } from 'atoms/buttons/Button';
import { Vertical } from 'atoms/layout/flex';
import { LabelL, LabelM } from 'atoms/typography';
import { useIsMediumMobileHeightAndAbove } from 'hooks/device/useIsMediumMobileHeightAndAbove';
import { glossary } from 'lib/glossary';
import { mediumMobileHeightAndAbove } from 'style/mediaQuery';

import { InteractiveStackedCards } from '../InteractiveStackedCards';
import packBottom from './assets/packBottom.png';
import topStripe from './assets/topStripe.png';

const Root = styled(Vertical).attrs({ center: true, gap: 2 })`
  width: 100%;
`;
const PackRoot = styled(animated.div)`
  --pack-aspect-ratio: 0.65217;
  --stripe-height: 50px;
  position: relative;
  isolation: isolate;
  width: 100%;
  aspect-ratio: var(--pack-aspect-ratio);
`;
const PackBottomImg = styled.img.attrs({ src: packBottom })`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  transition: opacity ease-in 1s 0.5s;

  &.hidden {
    opacity: 0;
  }
`;
const TopStripeImg = styled.img.attrs({ src: topStripe })`
  position: absolute;
  width: 100%;
  transform: translate(0, 1px);
  clip-path: polygon(
    var(--progressPercentage) 0%,
    100% 0%,
    100% 100%,
    calc(var(--progressPercentage) - ((1 - var(--progress) / 100) * 50px)) 100%
  );
`;
const DanglingStripeWrapper = styled.div`
  width: 100%;
  height: var(--stripe-height);
  position: absolute;
  transform-origin: top left;
  overflow: hidden;
  clip-path: polygon(0% 0%, 100% 0%, 100% 100%, 50px 100%);
  opacity: 1;
  transition: opacity ease-in 1s 0.5s;
  transform: translate(var(--progressPercentage))
    rotate(calc((1 - var(--progress) / 300) * 90deg));

  &.hidden {
    opacity: 0;
  }
`;
const DanglingStripeImg = styled.img.attrs({ src: topStripe })`
  width: 100%;
  position: absolute;
  transform: rotateY(-180deg) translate(calc(100% - var(--progressPercentage)));
`;
const DragDiv = styled.div`
  position: absolute;
  inset: 0;
  touch-action: none;
`;
const DashedLineWrapper = styled(Vertical)`
  position: absolute;
  top: var(--stripe-height);
  width: 100%;
  overflow: hidden;
`;
const DashedLineSVG = styled(animated.svg)`
  width: 100%;
  height: 3px;
  line {
    stroke: var(--c-white);
    stroke-width: 3px;
    stroke-dasharray: 5% 2%;
  }
`;
const StackedCardsWrapper = styled.div`
  height: 360px;
  transform: translateY(20px);
  @media ${mediumMobileHeightAndAbove} {
    height: 500px;
  }
`;

type Props = {
  cards: string[];
  onContinue: () => void;
};
export const RevealPackCards = ({ cards, onContinue }: Props) => {
  const isMediumMobileHeightAndAbove = useIsMediumMobileHeightAndAbove();
  const [pixelsTeared, setPixelsTeared] = useState(0);
  const [stripeTeared, setStripeTeared] = useState(false);
  const [showCards, setShowCards] = useState(false);

  const [tearAnimationSpring, tearAnimationSpringApi] = useSpring(() => ({
    '--progress': 0,
    '--progressPercentage': '0%',
  }));
  const [dashedLineStyle] = useSpring(() => ({
    from: {
      transform: `translate(-100%)`,
    },
    to: {
      transform: `translate(100%)`,
    },
    loop: true,
    config: config.slow,
  }));

  const animateProgress = (progress: number) => {
    tearAnimationSpringApi.start({
      '--progress': progress,
      '--progressPercentage': `${progress}%`,
    });
  };
  const fullyTear = () => {
    animateProgress(120);
    setStripeTeared(true);
  };
  const packRef = useRef<HTMLDivElement>(null);
  const bind = useDrag(
    ({ xy: [x], delta: [xDelta], initial: [gestureStartPosition], last }) => {
      if (packRef.current) {
        const { left: packLeftCoordinate, width } =
          packRef.current?.getBoundingClientRect() || {};
        const isMakingProgress = xDelta > 0 && x > pixelsTeared;
        const gestureStartsInAuthorizedZone =
          pixelsTeared === 0
            ? gestureStartPosition < packLeftCoordinate + width / 2
            : true;

        if (isMakingProgress && gestureStartsInAuthorizedZone) {
          const progress = (100 * (x - packLeftCoordinate)) / width;
          if (progress < 120) {
            animateProgress(progress);
          }
          if (progress >= 100 && !stripeTeared) {
            setStripeTeared(true);
          }
        }

        if ((last || xDelta < 0) && x > pixelsTeared) {
          setPixelsTeared(x);
        }
      }
    }
  );

  useEffect(() => {
    if (stripeTeared) {
      const timeout = setTimeout(() => {
        setShowCards(true);
      }, 1000);
      return () => {
        clearTimeout(timeout);
      };
    }
    return () => {};
  }, [stripeTeared]);

  if (showCards) {
    return (
      <Vertical center gap={8}>
        <StackedCardsWrapper>
          <InteractiveStackedCards
            cards={cards}
            cardWidth={isMediumMobileHeightAndAbove ? 240 : 180}
            restack
          />
        </StackedCardsWrapper>
        <Button onClick={onContinue} size="medium">
          <FormattedMessage {...glossary.continue} />
        </Button>
      </Vertical>
    );
  }

  return (
    <Root>
      <LabelL bold>
        <FormattedMessage id="OpenPack.swipe" defaultMessage="Swipe to open" />
      </LabelL>
      <PackRoot ref={packRef} style={tearAnimationSpring as CSSProperties}>
        <PackBottomImg className={classNames({ hidden: stripeTeared })} />
        <TopStripeImg />
        <DanglingStripeWrapper className={classNames({ hidden: stripeTeared })}>
          <DanglingStripeImg />
        </DanglingStripeWrapper>
        {pixelsTeared === 0 && (
          <DashedLineWrapper>
            <DashedLineSVG style={dashedLineStyle}>
              <line x1="0" y1="1.5" x2="40%" y2="1.5" />
            </DashedLineSVG>
          </DashedLineWrapper>
        )}
        <DragDiv {...bind()} />
      </PackRoot>
      <Button size="medium" color="tertiary" onClick={fullyTear}>
        <FormattedMessage id="OpenPack.open" defaultMessage="Open" />
      </Button>
      <LabelM className="text-center">
        Disclaimer: the pack image is not correct. This will be fixed in a
        future release
      </LabelM>
    </Root>
  );
};
